import React, { useEffect, useRef, useState } from 'react';
import useMobileInstallPrompt, {
  isIosPhone,
} from '../hooks/useMobileInstallPrompt';
import appIcon from '../assets/images/star123-icon.webp';
import './InstallAppPrompt.css';

const APK_FILE_NAME = 'Star123.apk';
const DEFAULT_APK =
  'https://d2elum7hsyw4cy.cloudfront.net/star123-v1.0.0.apk';

function triggerApkDownload(url) {
  if (!url || typeof document === 'undefined') return;
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.setAttribute('download', APK_FILE_NAME);
  anchor.rel = 'noopener noreferrer';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

function PlayLogo() {
  return (
    <svg className="iap-play-logo" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#34A853" d="M3.2 20.7 13.1 12 3.2 3.3c-.4.3-.7.9-.7 1.6v14.2c0 .7.3 1.3.7 1.6z" />
      <path fill="#FBBC04" d="m16.4 9.1-3.3 2.9 3.3 2.9 4.1-2.4c.7-.4.7-1.4 0-1.8L16.4 9.1z" />
      <path fill="#4285F4" d="M16.4 14.9 13.1 12 3.2 20.7c.5.4 1.2.4 1.9 0l11.3-5.8z" />
      <path fill="#EA4335" d="M16.4 9.1 5.1 3.3c-.7-.4-1.4-.4-1.9 0L13.1 12l3.3-2.9z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        fill="currentColor"
        d="M9.2 16.6 4.8 12.2l1.4-1.4 3 3 8-8 1.4 1.4-9.4 9.4z"
      />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 3v10.2l3.6-3.6 1.4 1.4-6 6-6-6 1.4-1.4 3.6 3.6V3h2zm-7 16h14v2H5v-2z"
      />
    </svg>
  );
}

function InstallAppPrompt({ appLink }) {
  const {
    isPhone,
    isVisible,
    openedManually,
    isIos,
    dismiss,
    promptNativeInstall,
  } = useMobileInstallPrompt();

  const [view, setView] = useState('install'); // install | progress | howto
  const [progress, setProgress] = useState(0);
  const [step1Done, setStep1Done] = useState(false);
  const [step2Done, setStep2Done] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [fallbackNote, setFallbackNote] = useState('');
  const timersRef = useRef([]);

  const apkUrl = appLink || DEFAULT_APK;
  const shouldShow = isVisible && (isPhone || openedManually);

  const clearTimers = () => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  };

  useEffect(() => {
    if (!shouldShow) {
      clearTimers();
      setView('install');
      setProgress(0);
      setStep1Done(false);
      setStep2Done(false);
      setShowIosHelp(false);
      setFallbackNote('');
    }
  }, [shouldShow]);

  useEffect(() => () => clearTimers(), []);

  if (!shouldShow) {
    return null;
  }

  const runProgressSequence = () => {
    clearTimers();
    setView('progress');
    setProgress(0);
    setStep1Done(false);
    setStep2Done(false);

    const ticks = [
      [180, 18],
      [420, 38],
      [720, 58],
      [1050, 78],
      [1400, 92],
      [1750, 100],
    ];

    ticks.forEach(([delay, value]) => {
      const id = window.setTimeout(() => {
        setProgress(value);
        if (value >= 40) setStep1Done(true);
        if (value >= 100) setStep2Done(true);
      }, delay);
      timersRef.current.push(id);
    });
  };

  const handleInstall = async () => {
    setFallbackNote('');
    setShowIosHelp(false);

    if (isIos || isIosPhone()) {
      setShowIosHelp(true);
      return;
    }

    if (apkUrl) {
      triggerApkDownload(apkUrl);
      runProgressSequence();
      return;
    }

    const result = await promptNativeInstall();
    if (result.available && result.outcome === 'accepted') {
      dismiss();
      return;
    }

    setFallbackNote(
      'Download link is not available right now. Please try again shortly.'
    );
  };

  const handleClose = () => {
    clearTimers();
    dismiss();
  };

  return (
    <div className="iap-root" data-install-prompt="star123">
      <button
        type="button"
        className="iap-backdrop"
        aria-label="Close download sheet"
        onClick={handleClose}
      />
      <aside
        className="iap-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={view === 'howto' ? 'iap-howto-title' : 'iap-app-name'}
      >
        <div className="iap-handle" aria-hidden="true" />
        <div className="iap-sheet-inner">
          <button
            type="button"
            className="iap-close"
            aria-label="Close install prompt"
            onClick={handleClose}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path
                d="M6.4 6.4 17.6 17.6M17.6 6.4 6.4 17.6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
            </svg>
          </button>

          {view === 'howto' ? (
            <div className="iap-howto">
              <div className="iap-howto-hero" aria-hidden="true">
                <span className="iap-howto-glow" />
                <DownloadIcon />
              </div>
              <h2 id="iap-howto-title" className="iap-howto-title">
                Open your Downloads
              </h2>
              <p className="iap-howto-sub">
                The APK is saved to your device. Follow these steps:
              </p>

              <ol className="iap-howto-steps">
                <li>
                  <span className="iap-howto-num">1</span>
                  <p>
                    Tap the <strong>⋮ menu</strong> in the top-right corner of
                    your browser.
                  </p>
                </li>
                <li>
                  <span className="iap-howto-num">2</span>
                  <p>
                    Select <strong>Downloads</strong> from the menu list.
                  </p>
                </li>
                <li>
                  <span className="iap-howto-num">3</span>
                  <p>
                    Tap the <strong>{APK_FILE_NAME}</strong> file to begin
                    installation.
                  </p>
                </li>
              </ol>

              <div className="iap-howto-note" role="note">
                <span className="iap-howto-note-icon" aria-hidden="true">
                  i
                </span>
                <p>
                  <strong>Allow unknown sources</strong> if prompted — required
                  to install APK files outside the Play Store.
                </p>
              </div>

              <button
                type="button"
                className="iap-secondary-btn"
                onClick={() => setView('progress')}
              >
                ← Back
              </button>
            </div>
          ) : (
            <>
              <div className="iap-header">
                <div className="iap-icon-wrap">
                  <img
                    className="iap-icon"
                    src={appIcon}
                    alt="Star123 App"
                    width={64}
                    height={43}
                    decoding="async"
                  />
                </div>
                <div className="iap-meta">
                  <div className="iap-platform">
                    <PlayLogo />
                    Android app
                  </div>
                  <h2 id="iap-app-name" className="iap-name">
                    Star123 App
                  </h2>
                  <p className="iap-developer">Star123 Official</p>
                  <p className="iap-verify">Verified official download</p>
                </div>
              </div>

              <div className="iap-stats">
                <div className="iap-stat">
                  <div className="iap-stat-value">
                    4.9
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        fill="#F9AB00"
                        d="M12 2.6 14.6 8.4l6.4.6-4.9 4.1 1.5 6.2L12 16.6 6.4 19.3l1.5-6.2L3 9l6.4-.6L12 2.6z"
                      />
                    </svg>
                  </div>
                  <span className="iap-stat-label">1122 reviews</span>
                </div>
                <div className="iap-stat">
                  <div className="iap-stat-value">103K+</div>
                  <span className="iap-stat-label">Downloads</span>
                </div>
                <div className="iap-stat">
                  <div className="iap-stat-value">
                    <span className="iap-age">18+</span>
                  </div>
                  <span className="iap-stat-label">Rated for 18+</span>
                </div>
              </div>

              {view === 'progress' ? (
                <div className="iap-progress-block">
                  <div
                    className="iap-progress-track"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={progress}
                    aria-label="Download progress"
                  >
                    <div
                      className="iap-progress-fill"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="iap-progress-pct">{progress}%</p>

                  <div
                    className={`iap-step-card${step1Done ? ' is-done' : ''}`}
                  >
                    <span className="iap-step-check" aria-hidden="true">
                      {step1Done ? <CheckIcon /> : null}
                    </span>
                    <div className="iap-step-copy">
                      <strong>Starting Download</strong>
                      <span>Connected to server</span>
                    </div>
                    {step1Done ? (
                      <span className="iap-step-badge">Done</span>
                    ) : null}
                  </div>

                  <div
                    className={`iap-step-card${step2Done ? ' is-done' : ''}`}
                  >
                    <span className="iap-step-check" aria-hidden="true">
                      {step2Done ? <CheckIcon /> : null}
                    </span>
                    <div className="iap-step-copy">
                      <strong>Download complete</strong>
                      <span>Check Downloads tab in browser.</span>
                    </div>
                    {step2Done ? (
                      <span className="iap-step-badge">Done</span>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    className="iap-secondary-btn iap-howto-trigger"
                    onClick={() => setView('howto')}
                    disabled={!step2Done}
                  >
                    <DownloadIcon />
                    How to find downloads
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="iap-install"
                  onClick={handleInstall}
                >
                  Install
                </button>
              )}

              <div className="iap-about">
                <h3 className="iap-about-title">
                  About this app
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M9 6l6 6-6 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </h3>
                <p id="iap-app-about" className="iap-about-text">
                  Get live results, charts and fast updates in one secure app.
                </p>
                <div className="iap-tags">
                  <span className="iap-tag">Live results</span>
                  <span className="iap-tag">Charts</span>
                  <span className="iap-tag">Fast updates</span>
                </div>
              </div>

              {showIosHelp && (
                <div className="iap-ios-help">
                  Safari on iPhone does not support APK install. To add Star123
                  to your Home Screen:
                  <ol>
                    <li>Tap the Share button</li>
                    <li>Choose Add to Home Screen</li>
                    <li>Tap Add</li>
                  </ol>
                </div>
              )}

              {fallbackNote && !showIosHelp && (
                <p className="iap-note">{fallbackNote}</p>
              )}

              <button type="button" className="iap-not-now" onClick={handleClose}>
                Not now
              </button>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

export default InstallAppPrompt;
